import {
  computeLeaderboards,
  computeMarketComments,
  computeNotableEvents,
  computeMarketSocialProof,
  computeMarketStats,
  computeMarketVoteHistory,
  computeSeasonZeroParticipants,
  createMockMarketComment,
  hideMockMarketComment,
  mockStore,
  reportMockMarketComment,
  toggleMockMarketCommentLike
} from "./mockData";
import { getPendingAffiliateId } from "./affiliate";
import { normalizeSupportCommunities, parseSupportCommunitiesFallback } from "./supportCommunities";
import { walletDebug } from "./walletDebug.js";
import { dappStoreMockDiscoverySnapshot } from "./dappStoreMockData.js";
import {
  MARKET_PAYMENT_TOKEN_IDS,
  getDefaultMarketPaymentTokenConfig,
  getMarketPaymentTokenConfig
} from "../../shared/marketTokens.js";

const listeners = new Set();
let session = null;
let backendAvailable = false;
const backendDisabled = import.meta.env.VITE_DISABLE_API === "1";
const isProd = import.meta.env.PROD === true;
const runtimeEnvironment = String(import.meta.env.ENVIRONMENT || "").trim().toLowerCase();
const isProductionRuntime = runtimeEnvironment ? runtimeEnvironment === "production" : isProd;
const MOCKABLE_AUTH_PATHS = new Set([
  "/api/auth/session",
  "/api/auth/nonce",
  "/api/auth/sign-in-nonce",
  "/api/auth/privy",
  "/api/auth/verify",
  "/api/auth/logout"
]);

export class TermsRequiredError extends Error {
  constructor(message, payload = {}) {
    super(message || "Please accept the current terms before continuing.");
    this.name = "TermsRequiredError";
    this.status = payload.status || 428;
    this.code = "terms_required";
    this.currentTermsVersionId = payload.currentTermsVersionId || "";
  }
}

export class CurrentTermsChangedError extends Error {
  constructor(message, payload = {}) {
    super(message || "The current terms changed. Please review them again.");
    this.name = "CurrentTermsChangedError";
    this.status = payload.status || 409;
    this.code = "current_terms_changed";
    this.currentTermsVersionId = payload.currentTermsVersionId || "";
  }
}

function emit(event) {
  for (const listener of listeners) {
    listener(event);
  }
}

export function onLocalEvent(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function callApi(path, options = {}) {
  if (backendDisabled && !shouldAllowDisabledApiRouteMock(path)) {
    await delayDisabledApiFallback();
    backendAvailable = false;
    const error = new Error("API disabled");
    error.status = 503;
    throw error;
  }

  const isFormData = options.body instanceof FormData;
  const debugAuthRoute = isWalletAuthDebugRoute(path);
  if (debugAuthRoute) {
    walletDebug("api.request", {
      path,
      method: options.method || "GET",
      hasAuthorization: Boolean(options.headers?.authorization)
    });
  }

  const response = await fetch(path, {
    credentials: "include",
    headers: {
      ...(isFormData ? {} : { "content-type": "application/json" }),
      ...(options.headers || {})
    },
    ...options
  });

  if (!response.ok) {
    backendAvailable = false;
    const payload = await response.json().catch(() => ({}));
    if (debugAuthRoute) {
      walletDebug("api.response.error", {
        path,
        status: response.status,
        code: payload?.code || "",
        error: payload?.error || "",
        requestId: payload?.requestId || response.headers.get("x-pm-auth-request-id") || ""
      });
    }
    throw normalizeApiError(payload, response.status);
  }

  const contentType = response.headers.get("content-type") || "";
  if (response.status === 204) {
    backendAvailable = true;
    return null;
  }

  if (!contentType.includes("application/json")) {
    backendAvailable = false;
    if (debugAuthRoute) {
      walletDebug("api.response.non_json", {
        path,
        status: response.status,
        contentType
      });
    }
    const error = new Error("API unavailable");
    error.status = 503;
    throw error;
  }

  backendAvailable = true;
  const data = await response.json();
  if (debugAuthRoute) {
    walletDebug("api.response.ok", {
      path,
      status: response.status,
      walletAddress: data?.walletAddress || "",
      userId: data?.userId || "",
      expiresAt: data?.expiresAt || ""
    });
  }
  return data;
}

function normalizeApiError(payload, status) {
  if (payload?.code === "terms_required") {
    return new TermsRequiredError(payload.error, { ...payload, status });
  }
  if (payload?.code === "current_terms_changed") {
    return new CurrentTermsChangedError(payload.error, { ...payload, status });
  }
  const error = new Error(payload?.error || `Request failed with ${status}`);
  error.status = status;
  error.code = payload?.code || "";
  error.payload = payload || {};
  return error;
}

function isWalletAuthDebugRoute(path) {
  try {
    const pathname = typeof window === "undefined" ? path : new URL(path, window.location.origin).pathname;
    return pathname === "/api/auth/privy" || pathname === "/api/auth/session";
  } catch {
    return path === "/api/auth/privy" || path === "/api/auth/session";
  }
}

function shouldAllowDisabledApiRouteMock(path) {
  if (!backendDisabled || typeof window === "undefined" || window.__PM_TEST_ENABLE_AUTH_ROUTE_MOCKS__ !== true) {
    return false;
  }

  try {
    return MOCKABLE_AUTH_PATHS.has(new URL(path, window.location.origin).pathname);
  } catch {
    return false;
  }
}

async function delayDisabledApiFallback() {
  if (typeof window === "undefined") return;

  const delayMs = Number(window.localStorage?.getItem("pm:mockApiDelayMs") || 0);
  if (!Number.isFinite(delayMs) || delayMs <= 0) return;

  await new Promise((resolve) => setTimeout(resolve, Math.min(delayMs, 3000)));
}

function throwIfAborted(signal) {
  if (!signal?.aborted) return;
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";
  throw error;
}

function fallbackInitSession() {
  if (!session) {
    session = null;
  }
  return session;
}

export async function fetchAuthSession() {
  try {
    const data = await callApi("/api/auth/session");
    session = data;
    return data;
  } catch (error) {
    if (error?.status === 401) {
      session = null;
      return null;
    }
    if (isProd) {
      throw new Error("Session fetch failed.");
    }
    return fallbackInitSession();
  }
}

export async function initSession() {
  return fetchAuthSession();
}

export async function requestAuthNonce(walletAddress) {
  return callApi("/api/auth/nonce", {
    method: "POST",
    body: JSON.stringify({ walletAddress })
  });
}

export async function requestWalletSignInNonce() {
  return callApi("/api/auth/sign-in-nonce", {
    method: "POST",
    body: "{}"
  });
}

export async function verifyWalletAuth({ walletAddress, nonce, signatureBase58, signedMessageBase58 = "" }) {
  const data = await callApi("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({
      walletAddress,
      nonce,
      signatureBase58,
      ...(signedMessageBase58 ? { signedMessageBase58 } : {})
    })
  });
  session = data;
  return data;
}

export async function verifyPrivyWalletAuth({ accessToken, walletAddress = "" }) {
  walletDebug("privy.verify.start", {
    hasAccessToken: Boolean(accessToken),
    walletAddress
  });
  const data = await callApi("/api/auth/privy", {
    method: "POST",
    headers: {
      authorization: `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      walletAddress
    })
  });
  session = data;
  walletDebug("privy.verify.success", {
    walletAddress: data?.walletAddress || "",
    userId: data?.userId || ""
  });
  return data;
}

export async function logoutAuthSession() {
  await callApi("/api/auth/logout", {
    method: "POST",
    body: "{}"
  });
  session = null;
}

export async function setNickname(nickname) {
  const safeNickname = nickname.trim().slice(0, 32);
  const data = await callApi("/api/profile/nickname", {
    method: "POST",
    body: JSON.stringify({ nickname: safeNickname })
  });
  session = { ...session, nickname: data.nickname };
  return data;
}

export async function startProfileXLink(redirectPath = "/profile") {
  return callApi("/api/profile/x-link/start", {
    method: "POST",
    body: JSON.stringify({ redirectPath })
  });
}

export async function disconnectProfileXLink() {
  return callApi("/api/profile/x-link/disconnect", {
    method: "POST",
    body: JSON.stringify({})
  });
}

export function getSession() {
  return session;
}

export function isBackendAvailable() {
  return backendAvailable;
}

export function getMarketPaymentTokenOptions() {
  return MARKET_PAYMENT_TOKEN_IDS.map((tokenId) => getMarketPaymentTokenConfig(tokenId));
}

export async function fetchMarketPaymentTokenOptions() {
  const payload = await callApi("/api/market-payment-tokens");
  return Array.isArray(payload?.tokens) ? payload.tokens : getMarketPaymentTokenOptions();
}

export function getDefaultMarketPaymentToken() {
  return getDefaultMarketPaymentTokenConfig();
}

export async function getCurrentTerms() {
  if (backendDisabled) {
    const { initialServiceTermsVersion } = await import("../../shared/serviceTermsContent.js");
    return {
      id: "00000000-0000-4000-8000-000000000511",
      versionLabel: initialServiceTermsVersion.versionLabel,
      title: initialServiceTermsVersion.title,
      summary: initialServiceTermsVersion.summary,
      updatedOn: initialServiceTermsVersion.content.updatedOn,
      effectiveAt: initialServiceTermsVersion.effectiveAt,
      contentSha256: initialServiceTermsVersion.contentSha256,
      content: initialServiceTermsVersion.content
    };
  }
  return callApi("/api/terms/current");
}

export async function getTermsVersion(termsVersionId) {
  return callApi(`/api/terms/${encodeURIComponent(termsVersionId)}`);
}

export async function acceptCurrentTerms({ termsVersionId, contentSha256, acceptanceSurface, acceptedFromPath }) {
  return callApi("/api/terms/acceptance", {
    method: "POST",
    body: JSON.stringify({ termsVersionId, contentSha256, acceptanceSurface, acceptedFromPath })
  });
}

export function isTermsPreconditionError(error) {
  return (
    error instanceof TermsRequiredError ||
    error instanceof CurrentTermsChangedError ||
    error?.code === "terms_required" ||
    error?.code === "current_terms_changed"
  );
}

export function getInitialMarketsSnapshot() {
  if (!backendDisabled) {
    return [];
  }
  return mockStore.markets.map(computeMarketStats);
}

export function getInitialLeaderboardsSnapshot() {
  return normalizeLeaderboardsSnapshot(
    backendDisabled
      ? computeLeaderboards()
      : {
          global: [],
          day: [],
          hour: [],
          market: {},
          affiliate: { allTime: [], day: [], hour: [] },
          community: { day: [], week: [], allTime: [] }
        }
  );
}

export function getInitialNotableEventsSnapshot() {
  if (!backendDisabled) {
    return [];
  }
  return computeNotableEvents({
    since: new Date(Date.now() - 60 * 60 * 1000).toISOString()
  });
}

export function getInitialDappStoreDiscoverySnapshot() {
  if (!backendDisabled) {
    return { run: null, apps: [], categories: [] };
  }
  return dappStoreMockDiscoverySnapshot;
}

export async function getDappStoreLatestRun() {
  if (backendDisabled) return dappStoreMockDiscoverySnapshot.run;
  return callApi("/api/dapp-store/runs/latest");
}

export async function listDappStoreApps() {
  if (backendDisabled) return dappStoreMockDiscoverySnapshot.apps;
  return callApi("/api/dapp-store/apps");
}

export async function listDappStoreCategories() {
  if (backendDisabled) return dappStoreMockDiscoverySnapshot.categories;
  return callApi("/api/dapp-store/categories");
}

export async function getDappStoreAppDetail(appKey, options = {}) {
  const safeKey = String(appKey || "").trim();
  if (backendDisabled) {
    throwIfAborted(options.signal);
    await delayDisabledApiFallback();
    throwIfAborted(options.signal);
    const app = dappStoreMockDiscoverySnapshot.apps.find((candidate) => candidate.appKey === safeKey) || null;
    return { app, history: app ? buildMockDappStoreHistory(app) : [] };
  }
  return callApi(`/api/dapp-store/apps/${encodeURIComponent(safeKey)}`, options);
}

export async function listMarkets() {
  try {
    return await callApi("/api/markets");
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load markets.");
    }
    return mockStore.markets.map(computeMarketStats);
  }
}

export async function getSupportCommunities() {
  try {
    return normalizeSupportCommunities(await callApi("/api/support-communities"));
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw error;
    }
    return parseSupportCommunitiesFallback();
  }
}

export async function getMarket(marketId) {
  try {
    return await callApi(`/api/markets/${marketId}`);
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load market.");
    }
    const market = mockStore.markets.find((item) => item.id === marketId || item.slug === marketId);
    if (!market) throw new Error("Market not found");
    return computeMarketStats(market);
  }
}

export async function getMarketVoteHistory(marketId) {
  try {
    return await callApi(`/api/markets/${marketId}/history`);
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load market vote history.");
    }

    const history = computeMarketVoteHistory(marketId);
    if (!history) throw new Error("Market not found");
    return history;
  }
}

export async function getMarketSocialProof(marketId) {
  try {
    return await callApi(`/api/markets/${marketId}/social-proof`);
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load market social proof.");
    }

    const socialProof = computeMarketSocialProof(marketId);
    if (!socialProof) throw new Error("Market not found");
    return socialProof;
  }
}

export async function getSeasonZero() {
  try {
    return await callApi("/api/season-0");
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load Season 0 participants.");
    }
    const participants = computeSeasonZeroParticipants();
    return {
      participantCount: participants.length,
      participants
    };
  }
}

export async function getNotableEvents() {
  try {
    const data = await callApi("/api/notable-events");
    return data?.events || [];
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      return [];
    }
    return computeNotableEvents({
      since: new Date(Date.now() - 60 * 60 * 1000).toISOString()
    });
  }
}

export async function getAdminNotableEvents() {
  const data = await callApi("/api/admin/notable-events");
  return data?.events || [];
}

export async function createAdminNotableEvent(input) {
  const data = await callApi("/api/admin/notable-events", {
    method: "POST",
    body: JSON.stringify(input)
  });
  return data?.event;
}

export async function getMarketComments({ marketId, sort = "top", cursor = "" }) {
  try {
    const search = new URLSearchParams({ sort });
    if (cursor) search.set("cursor", cursor);
    return await callApi(`/api/markets/${marketId}/comments?${search.toString()}`);
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load market comments.");
    }

    const comments = computeMarketComments({
      marketId,
      userId: currentSessionUserId(),
      sort
    });
    if (!comments) throw new Error("Market not found");
    return comments;
  }
}

export async function createMarketComment({ marketId, body }) {
  try {
    const created = await callApi(`/api/markets/${marketId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body })
    });
    emit({ type: "market_comments_updated", marketId });
    return created;
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      if (error?.status === 401) {
        throw new Error("Connect and authenticate your wallet to post a comment.");
      }
      throw new Error(error?.message || "Could not create comment.");
    }

    const created = createMockMarketComment({
      marketId,
      userId: currentSessionUserId(),
      body
    });
    emit({ type: "market_comments_updated", marketId });
    return created;
  }
}

export async function toggleMarketCommentLike({ marketId, commentId }) {
  try {
    return await callApi(`/api/markets/${marketId}/comments/${commentId}/like`, {
      method: "POST",
      body: "{}"
    });
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      if (error?.status === 401) {
        throw new Error("Connect and authenticate your wallet to like comments.");
      }
      throw new Error(error?.message || "Could not update comment like.");
    }

    return toggleMockMarketCommentLike({
      marketId,
      commentId,
      userId: currentSessionUserId()
    });
  }
}

export async function hideMarketComment({ marketId, commentId }) {
  try {
    return await callApi(`/api/markets/${marketId}/comments/${commentId}/hide`, {
      method: "POST",
      body: "{}"
    });
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      if (error?.status === 401) {
        throw new Error("Connect and authenticate your wallet to hide comments.");
      }
      throw new Error(error?.message || "Could not hide comment.");
    }

    return hideMockMarketComment({
      marketId,
      commentId,
      userId: currentSessionUserId()
    });
  }
}

export async function reportMarketComment({ marketId, commentId, reason, details }) {
  try {
    return await callApi(`/api/markets/${marketId}/comments/${commentId}/report`, {
      method: "POST",
      body: JSON.stringify({ reason, details })
    });
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      if (error?.status === 401) {
        throw new Error("Connect and authenticate your wallet to report comments.");
      }
      throw new Error(error?.message || "Could not report comment.");
    }

    return reportMockMarketComment({
      marketId,
      commentId,
      userId: currentSessionUserId(),
      reason,
      details
    });
  }
}

export async function createMarket(input) {
  try {
    const created = await callApi("/api/markets", {
      method: "POST",
      body: JSON.stringify(input)
    });
    emit({ type: "market_created", marketId: created.id });
    return created;
  } catch (error) {
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to create markets.");
    }
    throw new Error(error?.message || "Could not create market.");
  }
}

export async function createMarketCreationIntent(input) {
  return callApi("/api/market-creation-intents", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function submitMarketCreationTransaction({
  marketCreationIntentId,
  serializedTransactionBase64,
  expectedUserWallet
}) {
  try {
    const market = await callApi("/api/market-creation-submissions", {
      method: "POST",
      body: JSON.stringify({ marketCreationIntentId, serializedTransactionBase64, expectedUserWallet })
    });
    emit({ type: "market_created", marketId: market.id });
    return market;
  } catch (error) {
    if (isTermsPreconditionError(error)) {
      throw error;
    }
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to create markets.");
    }
    throw new Error("Could not create market. Please try again.");
  }
}

export async function presignEmbeddedMarketCreationTransaction(payload) {
  return callApi("/api/market-creation-submissions/embedded-presign", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function recordEmbeddedMarketCreationReceipt(payload) {
  try {
    const market = await callApi("/api/market-creation-submissions/embedded-receipt", {
      method: "POST",
      body: JSON.stringify(payload)
    });
    emit({ type: "market_created", marketId: market.id });
    return market;
  } catch (error) {
    if (isTermsPreconditionError(error)) {
      throw error;
    }
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to create markets.");
    }
    throw new Error("Could not create market. Please try again.");
  }
}

export async function uploadMarketImage(file) {
  validateImageFile(file);
  const contentType = inferImageContentType(file);

  try {
    const data = await callApi("/api/markets/upload-image", {
      method: "POST",
      headers: {
        "content-type": contentType
      },
      body: file
    });
    return data.imageUrl;
  } catch (error) {
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to upload images.");
    }
    if (isProductionRuntime) {
      throw new Error("Could not upload image.");
    }
    return fileToDataUrl(file);
  }
}

export async function createVoteIntent({ marketId, optionId, voteAmount, affiliateId, paymentTokenId }) {
  try {
    const body = { marketId, optionId, voteAmount, affiliateId };
    if (paymentTokenId) body.paymentTokenId = paymentTokenId;
    return await callApi("/api/vote-intents", {
      method: "POST",
      body: JSON.stringify(body)
    });
  } catch (error) {
    if (isTermsPreconditionError(error)) {
      throw error;
    }
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to vote.");
    }
    throw new Error("Vote failed. Please try again.");
  }
}

export async function submitSignedVoteTransaction({
  voteIntentId,
  serializedTransactionBase64,
  expectedUserWallet
}) {
  try {
    return await callApi("/api/votes", {
      method: "POST",
      body: JSON.stringify({ voteIntentId, serializedTransactionBase64, expectedUserWallet })
    });
  } catch (error) {
    if (isTermsPreconditionError(error)) {
      throw error;
    }
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to vote.");
    }
    if (error?.status === 409 && /blockhash expired|intent expired/i.test(error?.message || "")) {
      throw error;
    }
    throw new Error("Vote failed. Please try again.");
  }
}

export async function presignEmbeddedVoteTransaction(payload) {
  return callApi("/api/votes/embedded-presign", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function recordEmbeddedVoteReceipt(payload) {
  try {
    return await callApi("/api/votes/embedded-receipt", {
      method: "POST",
      body: JSON.stringify(payload)
    });
  } catch (error) {
    if (isTermsPreconditionError(error)) {
      throw error;
    }
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to vote.");
    }
    throw new Error("Vote failed. Please try again.");
  }
}

export async function castVote({ marketId, optionId, multiplier }) {
  const affiliateId = getPendingAffiliateId();
  const voteAmount = Number((Number(multiplier) * 0.01).toFixed(2));
  try {
    const updated = await callApi("/api/votes", {
      method: "POST",
      body: JSON.stringify({ marketId, optionId, voteAmount, affiliateId })
    });
    emit({ type: "market_updated", marketId });
    emit({ type: "leaderboards_updated" });
    return updated;
  } catch (error) {
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to vote.");
    }
    throw new Error(error?.message || "Vote submission failed.");
  }
}

export async function getLeaderboards(marketId) {
  try {
    const search = marketId ? `?marketId=${encodeURIComponent(marketId)}` : "";
    return normalizeLeaderboardsSnapshot(await callApi(`/api/leaderboards${search}`));
  } catch (error) {
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load leaderboards.");
    }
    return normalizeLeaderboardsSnapshot(computeLeaderboards());
  }
}

export async function getUserProfile() {
  try {
    return await callApi("/api/profile");
  } catch (error) {
    if (error?.status === 401) {
      throw new Error("Connect and authenticate your wallet to view your profile.");
    }
    if (!shouldUseDisabledApiFallback(error)) {
      throw new Error("Could not load profile.");
    }
    const userId = session?.walletAddress || session?.userId || "mock-wallet";
    return {
      user: {
        userId,
        walletAddress: userId,
        nickname: session?.nickname || null,
        xIdentity: null
      },
      metrics: {
        marketsEntered: 0,
        totalVotesCast: 0,
        totalSolVoted: 0,
        totalSolWon: 0,
        marketsWon: 0,
        closedMarketsParticipated: 0,
        winningPercentage: null,
        walletBalanceSol: null,
        affiliatePaymentsReceived: 0,
        affiliateSolReceived: 0
      },
      activeMarkets: [],
      closedMarkets: []
    };
  }
}

export async function recordWebVitalMetric(metric) {
  return callApi("/api/vitals", {
    method: "POST",
    body: JSON.stringify(metric)
  });
}

function shouldUseMockFallback(error) {
  if (!error) return true;
  if (error.status === 503) return true;
  if (error.name === "TypeError") return true;
  return false;
}

function shouldUseDisabledApiFallback(error) {
  if (backendDisabled) {
    return true;
  }
  if (isProd) {
    return false;
  }
  return shouldUseMockFallback(error);
}

function normalizeLeaderboardsSnapshot(snapshot = {}) {
  const affiliate = snapshot?.affiliate || {};
  const community = snapshot?.community || {};
  return {
    ...snapshot,
    global: Array.isArray(snapshot?.global) ? snapshot.global : [],
    day: Array.isArray(snapshot?.day) ? snapshot.day : [],
    hour: Array.isArray(snapshot?.hour) ? snapshot.hour : [],
    market: snapshot?.market && typeof snapshot.market === "object" ? snapshot.market : {},
    affiliate: {
      ...affiliate,
      allTime: Array.isArray(affiliate.allTime) ? affiliate.allTime : [],
      day: Array.isArray(affiliate.day) ? affiliate.day : [],
      hour: Array.isArray(affiliate.hour) ? affiliate.hour : []
    },
    community: {
      ...community,
      day: Array.isArray(community.day) ? community.day : [],
      week: Array.isArray(community.week) ? community.week : [],
      allTime: Array.isArray(community.allTime) ? community.allTime : []
    }
  };
}

function buildMockDappStoreHistory(app) {
  const reviews = Number.isFinite(app?.totalReviews) ? app.totalReviews : null;
  return [
    {
      appKey: app.appKey,
      runId: "00000000-0000-4000-8000-000000000201",
      appName: app.appName,
      asOf: "2026-04-10T21:45:00.000Z",
      starRating: app.starRating,
      totalReviews: reviews == null ? null : Math.max(0, reviews - 18),
      currentVersion: app.currentVersion,
      updatedOnDate: app.updatedOnDate,
      permissions: app.permissions,
      scrapeStatus: app.scrapeStatus
    },
    {
      appKey: app.appKey,
      runId: app.runId,
      appName: app.appName,
      asOf: app.asOf || dappStoreMockDiscoverySnapshot.run.asOf,
      starRating: app.starRating,
      totalReviews: reviews,
      currentVersion: app.currentVersion,
      updatedOnDate: app.updatedOnDate,
      permissions: app.permissions,
      scrapeStatus: app.scrapeStatus
    }
  ];
}

function currentSessionUserId() {
  return session?.walletAddress || session?.userId || null;
}

function validateImageFile(file) {
  if (!file) {
    throw new Error("Please select an image.");
  }
  if (!isAllowedImageFileCandidate(file)) {
    throw new Error("Image must be jpg, png, webp, gif, or avif.");
  }
  if (file.size > 250 * 1024) {
    throw new Error("Image must be under 250KB.");
  }
}

function isAllowedImageFileCandidate(file) {
  const allowedMimeTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
  const allowedExtensions = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".avif"];
  const fileType = String(file.type || "").toLowerCase();
  const fileName = String(file.name || "").toLowerCase();
  return allowedMimeTypes.includes(fileType) || allowedExtensions.some((extension) => fileName.endsWith(extension));
}

function inferImageContentType(file) {
  const fileType = String(file?.type || "").toLowerCase();
  if (["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"].includes(fileType)) {
    return fileType;
  }

  const fileName = String(file?.name || "").toLowerCase();
  if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) return "image/jpeg";
  if (fileName.endsWith(".png")) return "image/png";
  if (fileName.endsWith(".webp")) return "image/webp";
  if (fileName.endsWith(".gif")) return "image/gif";
  if (fileName.endsWith(".avif")) return "image/avif";
  return "application/octet-stream";
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image file."));
    reader.readAsDataURL(file);
  });
}
